import { ChangeTracker } from '../changes/ChangeTracker';
import { PlanSnapshot, TrackableObject } from '../changes/DomainChange';
import { newPlanId, PlanId } from '../changes/ids';
import { GenerationModelDetails } from '../dicom/dicomBase';
import { DicomPlan } from '../dicom/dicomPlan';
import { DoseMatrix } from './doseMatrix';
import { StructureSet } from './structureset';

/**
 * Domain object for an RT Plan.
 *
 * A plan owns no user-editable state; what changes over its lifetime are
 * its links to a {@link DoseMatrix} and a {@link StructureSet}. Those
 * links are established at ingest (DicomProcessor) and rewired by the
 * {@link Patient} aggregate when either side is removed.
 *
 * The link mutators deliberately do NOT emit changesets — same convention
 * as {@link StructureSet.createRoi}. Ingest runs outside any command()
 * context, so a self-marking setter would throw there; the Patient
 * cascade, which already runs inside a command, marks the plan instead.
 */
export class Plan implements TrackableObject {
    readonly trackableKind = 'plan' as const;

    /**
     * Unique runtime identifier generated when the {@link Plan} is
     * constructed. Accepts an existing id so a persisted plan can be
     * round-tripped without minting a new identity.
     */
    public readonly id: PlanId;

    private tracker: ChangeTracker | null = null;

    private _doseMatrix: DoseMatrix | null = null;

    private _structureSet: StructureSet | null = null;

    constructor(
        private readonly _dicomPlan: DicomPlan,
        public readonly sopInstanceUid: string,
        public readonly label: string,
        public readonly referencedStructureSetUid: string,
        id: PlanId = newPlanId()
    ) {
        this.id = id;
    }

    get trackableId(): PlanId {
        return this.id;
    }

    attachTracker(tracker: ChangeTracker): void {
        this.tracker = tracker;
    }

    captureSnapshot(): PlanSnapshot {
        return {
            id: this.id,
            sopInstanceUid: this.sopInstanceUid,
            doseId: this._doseMatrix?.id ?? null,
            structureSetId: this._structureSet?.id ?? null,
            isNew: this.isNew,
            isExported: this.isExported,
        };
    }

    public get doseMatrix(): DoseMatrix | null {
        return this._doseMatrix;
    }

    /**
     * Links (or unlinks, with `null`) the dose matrix computed for this
     * plan. Does not emit a changeset — see the class doc.
     */
    public linkDoseMatrix(doseMatrix: DoseMatrix | null): void {
        this._doseMatrix = doseMatrix;
    }

    public get structureSet(): StructureSet | null {
        return this._structureSet;
    }

    /**
     * Links (or unlinks, with `null`) the structure set this plan was
     * created against. Does not emit a changeset — see the class doc.
     */
    public linkStructureSet(structureSet: StructureSet | null): void {
        this._structureSet = structureSet;
    }

    public get dicom(): DicomPlan | null {
        return this._dicomPlan;
    }

    public get generationModel(): GenerationModelDetails | undefined {
        return this._dicomPlan.generationModel;
    }

    public get isNew(): boolean {
        return this._dicomPlan.isNew;
    }

    public get isExported(): boolean {
        return this._dicomPlan.isExported;
    }

    public setExported(): void {
        // Mark before mutating — see DoseMatrix.setExported.
        this.tracker?.markChanged(this);
        this._dicomPlan.setExported();
    }
}
